import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useConsole } from '@/console/ConsoleContext';
import { useRowRefreshStore } from '@/console/rowRefresh';
import { controllerId, conversationOwnerId, holderId, isOwnerUnknown, isOwnershipEnforced } from '@/utils/ownership';
import type { Conversation } from '@/types';

/**
 * WHO owns a conversation, as far as this console can PROVE - the one place the chat view and the contact panel
 * both read, so the two can never disagree about whether the owner is known.
 *
 * <b>The owner is the wire field.</b> `ownerAgentId` (utils/ownership.ts - no fallback to `assignedAgentId`) is what the
 * server said when the row was last read or pushed, kept current by the ownership pushes (`ownerVersion`) and by
 * `refreshRow`, the console's ONE way to re-read a row. There is no second detail-read mechanism here any more: this
 * file asks `refreshRow` and reads the answer from the same row everyone else reads.
 *
 * <b>Why a named owner can still be doubtful for a moment.</b> Until PR-A2 the server sends no ownership push, and the
 * state push that moves a row into HUMAN_TAKEOVER (conversationStore.handleSessionStateChanged) updates `state` ONLY. So
 * a row loaded when I owned the conversation, and that I released elsewhere, still says `ownerAgentId = me` when a
 * teammate takes it over - and without a check I would be offered the customer composer and a Release button on THEIR
 * customer. UX only, not security (the server does NOT refuse on ownership until PR-A2): this gate is what stands in
 * for it until then, and it stays correct once the pushes arrive (it then simply finds the answer already there).
 *
 * <b>The rule.</b> A NAMED owner of a human-controlled conversation is treated as UNKNOWN - which every consumer fails
 * closed on - while the console is re-reading that row (ANY `refreshRow` for it in flight: after an ownership push, a
 * refusal, a claim, or this view's own request) and, until the row has been re-read successfully, for the episode that
 * started on:
 *   - every transition INTO HUMAN_TAKEOVER seen while the view is mounted, and
 *   - MOUNT, when the row already reads HUMAN_TAKEOVER and names an owner (a row the tab has held for hours is exactly
 *     as stale as one that changed under us a second ago). A row that names nobody has no owner to distrust.
 * The distrust is a PURE function of the row, the in-flight markers and the shared record ({@link isOwnerUnverified}), so
 * there is never a render in which a stale owner is trusted: the first render after a transition or a mount already
 * reads "unknown".
 *
 * <b>"Confirming who owns this conversation" exists only while that read is pending.</b> The episode ends when its own
 * `refreshRow` answers: ONLY `applied` lifts the distrust. `superseded` does not (a newer read won the race; that is
 * not evidence this episode confirmed anything) - the read is asked again, at most twice more; a failure (network,
 * 5xx) or a hide (404) does NOT lift it either - unknown is not permission - but the bar stops SAYING "confirming"
 * (nothing is being confirmed any more) and shows the neutral read-only line instead. There is no retry until the
 * next transition or remount.
 *
 * <b>One driver, any number of readers.</b> {@link useOwnerVerification} (the chat view) records transitions and asks
 * for the re-read; {@link useVerifiedOwner} (the contact panel) only reads. A reader with no driver finds no record and
 * so fails closed - while the row is in flight it is unknown whatever any driver did.
 */

type OwnerFields = Pick<Conversation, 'sessionId' | 'state' | 'ownerAgentId'> &
  Partial<Pick<Conversation, 'partial' | 'ownershipEnforced' | 'assignedAgentId'>>;

/** What the driver has recorded about one open conversation. */
export interface OwnerTrack {
  /** The state the driver last saw. A row whose state differs has changed under the driver and is not yet recorded. */
  state: Conversation['state'];
  /** The owner is distrusted until this row has been re-read from the server. */
  unverified: boolean;
  /**
   * Names one distrust episode (a mount, or a transition into HUMAN_TAKEOVER). Drawn from ONE counter shared by every
   * conversation and never reused, so a late answer for an earlier episode - of this conversation or another - can
   * never be mistaken for the current one's and lift its distrust.
   */
  epoch: number;
  /** The episode's re-read ended WITHOUT lifting the distrust (it failed, or the row was hidden). Nothing is pending any more. */
  failed: boolean;
}

interface OwnerVerificationState {
  tracks: Record<string, OwnerTrack>;
  /**
   * The driver saw this row: first sight records it (`unverifiedAtMount` says whether its owner is distrusted), a later
   * sight with a CHANGED state is a transition - entering HUMAN_TAKEOVER distrusts the owner and starts a new epoch.
   */
  sync: (sessionId: string, state: Conversation['state'], unverifiedAtMount: boolean) => void;
  /** The row for episode `epoch` was re-read and applied: trust the owner again - unless a newer episode has begun. */
  verify: (sessionId: string, epoch: number) => void;
  /** The re-read for episode `epoch` ended without confirming anything: still unknown, but nothing is pending. */
  fail: (sessionId: string, epoch: number) => void;
  /** The view left this conversation. */
  forget: (sessionId: string) => void;
}

let epochSeq = 0;

/** How many extra reads an episode asks for when its read is `superseded` (it lifts nothing) before it gives up as unconfirmed. */
const MAX_SUPERSEDED_REASKS = 2;

export const useOwnerVerificationStore = create<OwnerVerificationState>((set) => ({
  tracks: {},
  sync: (sessionId, state, unverifiedAtMount) =>
    set((s) => {
      const t = s.tracks[sessionId];
      if (!t) {
        return {
          tracks: {
            ...s.tracks,
            [sessionId]: {
              state,
              unverified: unverifiedAtMount,
              epoch: unverifiedAtMount ? ++epochSeq : 0,
              failed: false,
            },
          },
        };
      }
      if (t.state === state) return s;
      const entering = state === 'HUMAN_TAKEOVER';
      return {
        tracks: {
          ...s.tracks,
          [sessionId]: { state, unverified: entering, epoch: entering ? ++epochSeq : t.epoch, failed: false },
        },
      };
    }),
  verify: (sessionId, epoch) =>
    set((s) => {
      const t = s.tracks[sessionId];
      if (!t || t.epoch !== epoch || !t.unverified) return s;
      return { tracks: { ...s.tracks, [sessionId]: { ...t, unverified: false, failed: false } } };
    }),
  fail: (sessionId, epoch) =>
    set((s) => {
      const t = s.tracks[sessionId];
      if (!t || t.epoch !== epoch || !t.unverified || t.failed) return s;
      return { tracks: { ...s.tracks, [sessionId]: { ...t, failed: true } } };
    }),
  forget: (sessionId) =>
    set((s) => {
      if (!s.tracks[sessionId]) return s;
      const rest = { ...s.tracks };
      delete rest[sessionId];
      return { tracks: rest };
    }),
}));

/**
 * The row names someone who could be a stale row's leftover: the owner, and - in legacy shared mode, where the live
 * controller (`assignedAgentId`) is who holds a takeover (utils/ownership.ts holderId) - the controller. A blank id
 * names nobody.
 */
const hasNamedOwner = (conv: OwnerFields) =>
  conversationOwnerId(conv) !== null || (!isOwnershipEnforced(conv) && controllerId(conv) !== null);

/**
 * Is this row's owner distrusted RIGHT NOW? Pure: the row, what the driver has recorded (`undefined` = nobody has), and
 * whether the console is re-reading this row at this moment. Only a human-controlled conversation has an owner to
 * distrust.
 */
export function isOwnerUnverified(conv: OwnerFields, track: OwnerTrack | undefined, refreshing = false): boolean {
  // A row the server has never described (a push or a draft placeholder, or an enforced answer with no owner stated)
  // has no owner to trust, in ANY state: unknown fails closed until the row is re-read and replaced.
  if (isOwnerUnknown(conv)) return true;
  if (conv.state !== 'HUMAN_TAKEOVER') return false;
  // A re-read of this row is queued or in flight (an ownership push, a refusal, a claim): whatever the row says now is
  // about to be replaced by the server's word, so it is not trusted in the meantime.
  if (refreshing) return true;
  // Nothing recorded: first sight of this row. Distrust a named owner (the row may be hours old); a row that names
  // nobody has no owner to distrust.
  if (!track) return hasNamedOwner(conv);
  // The state moved since the driver last looked, so this IS a transition into HUMAN_TAKEOVER that is not recorded yet.
  if (track.state !== conv.state) return true;
  return track.unverified;
}

export interface VerifiedOwner {
  /** The owner, or `null` when nobody can be named OR the owner is not yet verified. Consumers must fail closed on null. */
  owner: string | null;
  /**
   * WHO HOLDS the conversation for "may I act on it" (utils/ownership.ts holderId): the verified owner under private
   * ownership, the verified live controller (`assignedAgentId`) in legacy shared mode - the two differ when a teammate
   * took over a conversation someone else had guided. `null` when nobody can be named OR it is not yet verified; during
   * HUMAN_TAKEOVER that is UNKNOWN and every consumer fails closed on it. The lock, the bar's mode and the release
   * button read THIS; "return to team" and the owner row read `owner`.
   */
  holder: string | null;
  /** True while a named owner is distrusted (the server has not yet confirmed it). */
  ownerUnverified: boolean;
  /**
   * True while the confirmation is actually PENDING (a distrusted owner whose re-read has not ended): the only time
   * the bar may say "Confirming who owns this conversation". A distrusted owner whose re-read FAILED is still unknown
   * (`ownerUnverified`), but nothing is being confirmed, so this is false.
   */
  ownerConfirming: boolean;
}

/** READ the verified owner. Never reloads anything - the chat view drives that. */
export function useVerifiedOwner(conv: OwnerFields): VerifiedOwner {
  const track = useOwnerVerificationStore((s) => s.tracks[conv.sessionId]);
  const refreshing = useRowRefreshStore((s) => (s.inflight[conv.sessionId] ?? 0) > 0);
  const ownerUnverified = isOwnerUnverified(conv, track, refreshing);
  // Pending = being re-read now, or the episode has not yet ended in a failure. A row the server answered WITHOUT an
  // owner (so it is still unknown after its episode's re-read was applied) has nothing pending either: it stays unknown
  // and read-only, and says so plainly instead of "confirming" for ever.
  const settledUnknown = isOwnerUnknown(conv) && !refreshing && track !== undefined && !track.unverified;
  const ownerConfirming = ownerUnverified && !settledUnknown && (refreshing || track?.failed !== true);
  return {
    owner: ownerUnverified ? null : conversationOwnerId(conv),
    holder: ownerUnverified ? null : holderId(conv),
    ownerUnverified,
    ownerConfirming,
  };
}

/**
 * DRIVE the verification (mount this ONCE per open conversation - the chat view): record transitions and ask the
 * console to re-read the open row (`refreshRow`), lifting the distrust only when that re-read was applied. Returns what
 * {@link useVerifiedOwner} returns, for the driver's own use.
 */
export function useOwnerVerification(conv: OwnerFields): VerifiedOwner {
  const { refreshRow } = useConsole();
  const sid = conv.sessionId;
  const track = useOwnerVerificationStore((s) => s.tracks[sid]);
  const sync = useOwnerVerificationStore((s) => s.sync);
  const forget = useOwnerVerificationStore((s) => s.forget);

  const result = useVerifiedOwner(conv);
  const { holder, ownerUnverified } = result;

  // The record lives exactly as long as the view is on this conversation: coming back to it later is a fresh mount,
  // and a row held while another conversation was open is as stale as any other.
  useEffect(() => () => forget(sid), [sid, forget]);

  // Record what this row says. The first sight is the MOUNT (a stale-at-mount owner is distrusted); a later change of
  // state is a transition. Keyed on the state only: the other row fields are read at that moment.
  useEffect(() => {
    sync(sid, conv.state, isOwnerUnverified(conv, undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, conv.state, sync]);

  const verifying = !!track?.unverified;
  const epoch = track?.epoch ?? 0;

  // The verification: ask the console to re-read THIS row, once per episode. Keyed on the episode only: re-running on
  // every unrelated render would re-fire the request. The answer is applied to the shared row BEFORE this resolves, so
  // no render sees the verified flag with the stale row; a failure or a hide does not lift anything.
  useEffect(() => {
    if (!verifying) return;
    void (async () => {
      // Only an answer the console actually APPLIED is evidence about the owner. `superseded` is not: it says a NEWER read
      // of the row won the race, not that this episode's read confirmed anything (and the newer one may have been refused
      // by the store, or may belong to nobody who is verifying). So it lifts nothing - the read is simply asked again,
      // which is by construction the newest, and a bounded number of times so a row that keeps being superseded ends
      // as unconfirmed (read-only, no "confirming" line) instead of looping.
      for (let attempt = 0; attempt <= MAX_SUPERSEDED_REASKS; attempt += 1) {
        const outcome = await refreshRow(sid);
        const records = useOwnerVerificationStore.getState();
        if (outcome === 'applied') {
          records.verify(sid, epoch);
          return;
        }
        if (outcome !== 'superseded') {
          records.fail(sid, epoch);
          return;
        }
        // The episode ended while we waited (a transition began a newer one, or the view left): nothing to ask for.
        const t = records.tracks[sid];
        if (!t || t.epoch !== epoch || !t.unverified) return;
      }
      useOwnerVerificationStore.getState().fail(sid, epoch);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, epoch, verifying]);

  // A human holds the conversation and the row names no HOLDER (the owner under private ownership, the live controller in
  // legacy shared mode). On today's server that can be a RACE, not a fault: the
  // takeover claim and the assignee are written a moment AFTER issueTakeover returns, so the re-read doTakeover fired can
  // land first and hand back the stale AI_HANDLING row; the live state push then moves only `state`. Without this the
  // taker would sit in HUMAN_TAKEOVER with no composer (the bar fails closed on an unknown owner) and nothing would
  // ever re-read the row. So learn the owner from the server again, ONCE per session per episode: the ref is keyed by
  // session and cleared as soon as an owner is known (or the state leaves takeover), so a row that is genuinely unowned
  // costs one re-read, never a loop. NOT while the owner is merely unverified (the effect stands down): the
  // verification above is already reading the row. This re-read never lifts a distrust and so is not a trust decision.
  const needsOwner = conv.state === 'HUMAN_TAKEOVER' && !holder;
  const ownerReloadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (ownerUnverified) return;
    if (!needsOwner) {
      ownerReloadedFor.current = null;
      return;
    }
    if (ownerReloadedFor.current === sid) return;
    ownerReloadedFor.current = sid;
    void refreshRow(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsOwner, ownerUnverified, sid]);

  return result;
}
